<template lang="pug">
.cms-api-tokens
    h4 {{ $t('CMS API Tokens') }}
    p.description {{ $t('API tokens authenticate external systems. A token is shown only once when created.') }}

    .token-list(v-if="tokens.length")
        .token-row(v-for="token in tokens" :key="token.id")
            .token-info
                strong {{ token.name }}
                .meta
                    span {{ $t('Created') }}: {{ formatDate(token.created_at) }}
                    span(v-if="token.last_used_at")  · {{ $t('Last used') }}: {{ formatDate(token.last_used_at) }}
                .created-token(v-if="newlyCreatedTokenId === token.id")
                    .token-warning
                        i.el-icon-warning
                        |  {{ $t('Copy this token now. It will not be shown again.') }}
                    .token-display
                        code {{ newlyCreatedToken }}
                        el-button(size="mini" type="text" @click="copyNewToken")
                            i(:class="copiedNewToken ? 'el-icon-check' : 'el-icon-document-copy'")
            el-button(
                circle
                type="danger"
                size="mini"
                icon="el-icon-fa-trash-alt far"
                @click="confirmDelete(token)"
            )

    .no-tokens(v-else-if="!loading")
        p {{ $t('No API tokens created yet.') }}

    .actions
        el-button(type="primary" size="small" @click="showCreateDialog = true")
            i.el-icon-plus
            |  {{ $t('Create Token') }}

    el-dialog(
        :title="$t('Create API Token')"
        :visible.sync="showCreateDialog"
        width="400px"
        :close-on-click-modal="false"
    )
        el-form(@submit.native.prevent="createToken")
            el-form-item.no-margin(:label="$t('Token Name')")
                el-input(v-model="newTokenName" autofocus)
        template(#footer)
            el-button(@click="showCreateDialog = false") {{ $t('Cancel') }}
            el-button(type="primary" @click="createToken" :loading="creating" :disabled="!newTokenName.trim()") {{ $t('Create') }}
</template>

<i18n>
{
    "de": {
        "CMS API Tokens": "CMS API Tokens",
        "API tokens authenticate external systems. A token is shown only once when created.": "API-Tokens authentifizieren externe Systeme. Ein Token wird nur einmalig bei der Erstellung angezeigt.",
        "No API tokens created yet.": "Noch keine API-Tokens erstellt.",
        "Copy this token now. It will not be shown again.": "Kopieren Sie diesen Token jetzt. Er wird nicht erneut angezeigt.",
        "Create Token": "Token erstellen",
        "Create API Token": "API-Token erstellen",
        "Token Name": "Token-Name",
        "Created": "Erstellt",
        "Last used": "Zuletzt verwendet",
        "Create": "Erstellen",
        "Cancel": "Abbrechen",
        "Delete Token": "Token löschen",
        "Are you sure you want to delete this token? Any systems using it will lose access.": "Sind Sie sicher, dass Sie diesen Token löschen möchten? Alle Systeme, die ihn verwenden, verlieren den Zugriff."
    }
}
</i18n>

<script>
import _ from 'lodash'
import { useSetupStore } from '@/stores/setup'
import { adminClient as apolloClient } from '@/store/apollo'
import CmsApiTokensGql from '@/extensions/chatbot/store/graphql/queries/CmsApiTokens.gql'
import CreateCmsApiTokenGql from '@/extensions/chatbot/store/graphql/mutations/CreateCmsApiToken.gql'
import DeleteCmsApiTokenGql from '@/extensions/chatbot/store/graphql/mutations/DeleteCmsApiToken.gql'

export default {
    name: 'CmsApiTokens',
    data() {
        return {
            loading: false,
            creating: false,
            tokens: [],
            showCreateDialog: false,
            newTokenName: '',
            newlyCreatedToken: null,
            newlyCreatedTokenId: null,
            copiedNewToken: false
        }
    },
    computed: {
        activeCms() {
            return useSetupStore().activeCms
        },
        cmsId() {
            const id = _.get(this.activeCms, 'id')
            return id ? parseInt(id) : null
        }
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return '—'
            return new Date(dateString).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })
        },
        async loadTokens() {
            if (!this.cmsId) return
            this.loading = true

            try {
                const response = await apolloClient.query({
                    query: CmsApiTokensGql,
                    variables: { cms_id: this.cmsId },
                    fetchPolicy: 'network-only'
                })
                this.tokens = _.get(response, 'data.CmsApiTokens', [])
            } catch (error) {
                console.error('Error loading API tokens:', error)
            } finally {
                this.loading = false
            }
        },
        async createToken() {
            if (!this.newTokenName.trim()) return
            this.creating = true

            try {
                const response = await apolloClient.mutate({
                    mutation: CreateCmsApiTokenGql,
                    variables: {
                        cms_id: this.cmsId,
                        name: this.newTokenName.trim()
                    }
                })

                const result = _.get(response, 'data.CreateCmsApiToken')
                this.newlyCreatedToken = result.plainTextToken
                this.newlyCreatedTokenId = result.id
                this.showCreateDialog = false
                this.newTokenName = ''
                this.loadTokens()
            } catch (error) {
                console.error('Error creating API token:', error)
                this.$notify.error({
                    title: 'Error creating token',
                    message: error.message,
                    position: 'bottom-left',
                    customClass: 'dark'
                })
            } finally {
                this.creating = false
            }
        },
        async confirmDelete(token) {
            try {
                await this.$confirm(
                    this.$t('Are you sure you want to delete this token? Any systems using it will lose access.'),
                    this.$t('Delete Token'),
                    { type: 'warning' }
                )
                await this.deleteToken(token)
            } catch {
                // cancelled
            }
        },
        async deleteToken(token) {
            try {
                await apolloClient.mutate({
                    mutation: DeleteCmsApiTokenGql,
                    variables: {
                        cms_id: this.cmsId,
                        token_id: token.id
                    }
                })

                if (this.newlyCreatedTokenId === token.id) {
                    this.newlyCreatedToken = null
                    this.newlyCreatedTokenId = null
                }

                this.loadTokens()
            } catch (error) {
                console.error('Error deleting API token:', error)
            }
        },
        copyNewToken() {
            navigator.clipboard.writeText(this.newlyCreatedToken)
            this.copiedNewToken = true
            setTimeout(() => { this.copiedNewToken = false }, 2000)
        }
    },
    watch: {
        cmsId: {
            immediate: true,
            handler(value) {
                if (value) this.loadTokens()
            }
        }
    }
}
</script>

<style lang="sass" scoped>
.cms-api-tokens
    margin-bottom: 20px

    h4
        margin: 0 0 4px
        font-size: 1rem
        font-weight: 600

    .description
        color: #909399
        font-size: 0.875rem
        margin: 0 0 16px

.token-list
    margin-bottom: 12px

.token-row
    display: flex
    align-items: center
    justify-content: space-between
    padding: 10px 12px
    background: #f9fafb
    border: 1px solid #ebeef5
    border-radius: 4px
    margin-bottom: 6px

    .token-info
        flex: 1
        min-width: 0

        strong
            font-size: 0.875rem

        .meta
            font-size: 0.75rem
            color: #909399
            margin-top: 2px

    .created-token
        margin-top: 8px

        .token-warning
            color: #e6a23c
            font-size: 0.8125rem
            margin-bottom: 6px

            .el-icon-warning
                margin-right: 4px

        .token-display
            display: flex
            align-items: center
            gap: 6px

            code
                flex: 1
                padding: 6px 8px
                background: #fdf6ec
                border: 1px solid #f5dab1
                border-radius: 3px
                font-size: 0.75rem
                word-break: break-all
                user-select: all

.no-tokens
    p
        color: #909399
        font-size: 0.875rem
        margin-bottom: 12px

.actions
    margin-top: 4px
</style>
